import { Message } from 'i18n';
import * as React from 'react';
import { FormProvider, UseFormMethods } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import rtl from 'styled-components-rtl';
import { Field, TagsField } from 'components/forms';
import { useRouter } from 'hooks';
import Icon from 'components/Icon';
import {
  SupplierOnboardingAction,
  useSupplierOnboardingDispatch,
  useSupplierOnboardingState,
} from 'context/supplierOnboardingContext';
import { PopupModal, useCalendlyEventListener } from 'react-calendly';
import DragHomeButton from 'pages/appClient/onboarding/shared/DragHomeButton';
import ScheduleMeetingPicker from 'pages/appClient/supplier-info/components/ScheduleMeetingPicker';
import ColorField from '../../../appManagement/common/FormFields/ColorField';
import SkipConfirmationDialog from '../components/SkipConfirmationDialog';
import ImagePicker from '../components/ImagePicker';

const SupplierMarketingInfo: React.FC<{
  onSubmit: VoidFunction;
  methods: UseFormMethods<{
    specification: File[];
    color: string;
    tags: string[];
    series: string;
  }>;
  isLoading: boolean;
}> = ({ onSubmit, methods, isLoading }) => {
  const [isConfirmationModalVisible, setIsGoBackModalVisible] = React.useState(
    false
  );
  const [schedulePickerOpen, setSchedulePickerOpen] = React.useState(false);
  const state = useSupplierOnboardingState();
  const { history } = useRouter();
  const dispatch = useSupplierOnboardingDispatch();
  const intl = useIntl();
  const { control, setValue } = methods;

  useCalendlyEventListener({
    onEventScheduled: e => {
      dispatch({
        type: SupplierOnboardingAction.SET_IS_SHEDULED_MEETING,
        payload: true,
      });
    },
  });

  return (
    <>
      <ParentDiv>
        <Header>
          <SkipText onClick={() => setIsGoBackModalVisible(true)}>
            <FormattedMessage id={Message.PROPERTY_ADD_PROPERTY_BUTTON_SKIP} />
          </SkipText>
          <UploadProductText>
            <FormattedMessage
              id={Message.SUPPLIER_UPLOAD_YOUR_FIRST_PRODUCT}
              values={{
                span: (chunks: string) => (
                  <span style={{ color: '#FC5C42' }}>{chunks}</span>
                ),
              }}
            />
          </UploadProductText>
          <MainDetailsText>
            <FormattedMessage id={Message.SUPPLIER_MARKETING_INFO} />
          </MainDetailsText>
        </Header>
        <MainDetails>
          <FormProvider {...methods}>
            <FormWrapperContainer onSubmit={onSubmit}>
              <ImagePickerDiv>
                <LogoParent>
                  <UploadLogoText>
                    <FormattedMessage
                      id={Message.SUPPLIER_UPLOAD_SPECIFICATION_FILE_LABEL}
                    />
                  </UploadLogoText>
                  <ImagePicker
                    name="specification"
                    label="Upload specification files"
                    control={control}
                    onFileChange={e => {
                      if (e?.target?.files)
                        setValue('specification', e?.target.files);
                    }}
                  />
                </LogoParent>
              </ImagePickerDiv>
              <FormFieldDiv
                label={{
                  title: intl.formatMessage({
                    id: Message.SUPPLIER_SERIES_LABEL,
                  }),
                  isBold: true,
                }}
                name="series"
                type="text"
                placeholder={intl.formatMessage({
                  id: Message.SUPPLIER_SERIES_PLACEHOLDER,
                })}
              />
              <ParentColorField>
                <ColorField name="color" width={327} required={false} />
              </ParentColorField>
              <TagsField
                required={false}
                name="tags"
                defaultValue={state.addProductDetails.tags ?? []}
                label={intl.formatMessage({
                  id: Message.SUPPLIER_TAGS_LABEL,
                })}
              />
              <FooterParent>
                <BackButtonParent>
                  <BackButton onClick={() => history.goBack()}>
                    <BackButtonText>
                      <FormattedMessage
                        id={Message.PROPERTY_ADD_PROPERTY_BUTTON_PREVIOUS}
                      />
                    </BackButtonText>
                  </BackButton>
                </BackButtonParent>
                <NextButtonParent>
                  <NextButton
                    disabled={!methods.formState.isValid || isLoading}
                  >
                    {isLoading ? (
                      <CustomIcon name="spinner" size={20} />
                    ) : (
                      <NextButtonText>
                        <FormattedMessage
                          id={Message.PROPERTY_ADD_PROPERTY_BUTTON_NEXT}
                        />
                      </NextButtonText>
                    )}
                    {/* <NextButtonText>
                      <FormattedMessage
                        id={Message.PROPERTY_ADD_PROPERTY_BUTTON_NEXT}
                      />
                    </NextButtonText> */}
                  </NextButton>
                </NextButtonParent>
              </FooterParent>
            </FormWrapperContainer>
          </FormProvider>
        </MainDetails>
      </ParentDiv>
      <DragHomeButton />
      <ScheduleMeetingPicker onClick={() => setSchedulePickerOpen(true)} />
      <PopupModal
        open={schedulePickerOpen}
        url="https://calendly.com/dan1707"
        rootElement={document.getElementById('root')!}
        onModalClose={() => {
          setSchedulePickerOpen(false);
        }}
      />
      <SkipConfirmationDialog
        isOpen={isConfirmationModalVisible}
        toggle={() => setIsGoBackModalVisible(prev => !prev)}
      />
    </>
  );
};

export default SupplierMarketingInfo;

const ParentDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  font-family: 'Roboto Condensed';
  .MuiFormControl-root {
    width: 327px;
    margin-top: 8px;
  }
  .combobox {
    label {
      margin-bottom: 0;
      font-weight: 600;
      font-size: 16px;
      line-height: 20px;
      color: #3e3e3e;
      margin-left: 0;
    }
  }
  .image-picker {
    width: 327px;
    height: 92px;
    margin-bottom: 0;
    border: 1px dashed #e8eaeb;
    label {
      span {
        font-size: 16px;
        line-height: 20px;
      }
      svg {
        fill: #3e3e3e;
        opacity: 1;
      }
    }
  }
  .maindetail-tag {
    margin-top: 8px;
    label {
      font-weight: 600;
      font-size: 16.5px;
      line-height: 20px;
      color: #3e3e3e;
      margin-left: 0;
      font-family: 'Roboto Condensed';
    }
    span {
      padding-right: 0;
    }
    .MuiFormControl-root {
      margin-top: 0;
    }
  }
`;

const SkipText = styled.div`
  text-decoration-line: underline;
  color: #696969;
  cursor: pointer;
  font-size: 18px;
  line-height: 22px;
  margin-left: auto;
  margin-top: 8px;
`;

export const MainDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const UploadProductText = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 28px;
  margin-top: 16px;
`;

export const MainDetailsText = styled.div`
  color: #3e3e3e;
  font-size: 18px;
  line-height: 22px;
  margin-top: 4px;
`;

export const FormWrapperContainer = styled.form.attrs({
  className: 'form-container',
})`
  margin-top: 16px !important;
  display: flex;
  flex-direction: column;
  @media (min-width: 1150px) {
    margin-top: 20px;
  }

  .phoneNumber {
    margin: -6px 0 24px;
    & .flag-dropdown,
    & .flag-dropdown.open {
      height: 48px;
    }

    & .form-control {
      height: 48px;
    }
  }
  .country-list {
    & .search {
      & .search-box {
        height: 48px;
      }
    }
  }
  && .submit-btn {
    @media (min-width: 1150px) {
      margin-top: 0;
      width: unset;
      ${rtl`
          margin-left: auto;
        `}
    }
  }
  & .divider-wrapper {
    margin: 14px auto;
  }

  & .anchor-link {
    color: #2277cc;
    font-weight: 500;
  }

  & .actions-container {
    @media (min-height: 800px) {
      margin-top: 0;
    }
    @media (min-width: 1150px) {
      margin-top: 15px;
    }
  }
  .dashed-icon {
    width: 48px;
    height: 48px;
    border: 1px dashed #3e3e3e;
  }
`;

export const InfoView = styled.div`
  height: 21.33px;
  width: 21.33px;
  margin-left: 6px;
  cursor: pointer;
`;

export const InfoImage = styled.img`
  height: 100%;
  width: 100%;
`;

export const ImagePickerDiv = styled.div`
  display: flex;
  justify-content: center;
  label {
    width: 100%;
  }
`;

export const LogoParent = styled.div``;

export const UploadLogoText = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 22px;
  color: #3e3e3e;
`;

export const FooterParent = styled.div`
  display: flex;
  justify-content: space-between;
  text-align: center;
  position: absolute;
  bottom: 24px;
  right: 0;
  width: 100%;
  padding: 0 24px;
`;

export const BackButtonParent = styled.div``;

export const BackButton = styled.button`
  border: 1px solid #d9d9d9;
  border-radius: 15px;
  padding: 9px 47px;
  height: 40px;
  width: 134px;
  text-align: center;
  background-color: white;
  cursor: pointer;
`;

export const BackButtonText = styled.div`
  font-size: 16px;
  line-height: 19px;
  color: #696969;
`;

export const NextButtonParent = styled.div`
  text-align: center;
`;

export const NextButton = styled.button`
  :disabled {
    background: #d9d9d9;
    cursor: default;
  }
  background: #fc5c42;
  border: 1px solid #d9d9d9;
  border-radius: 15px;
  padding: 9px 48px;
  height: 40px;
  width: 134px;
  text-align: center;
  cursor: pointer;
`;

export const NextButtonText = styled.div`
  font-weight: 500;
  font-size: 18px;
  line-height: 21px;
  color: rgba(255, 255, 255, 0.9);
`;

export const FormFieldDiv = styled(Field)`
  label {
    font-weight: 600;
    font-size: 18px;
    line-height: 22px;
    color: #3e3e3e;
    margin-left: 0;
    font-family: 'Roboto Condensed';
  }
  .MuiInputBase-root {
    margin-top: 20px;
    width: 327px;
    height: 48px;
    input {
      background: #fafcfe;
    }
  }
`;

const ParentColorField = styled.div`
  margin-top: 8px;
  .form-field-container {
    margin-top: -5px;
  }
  label {
    font-weight: 600;
    font-size: 16.5px;
    line-height: 20px;
    margin-left: 0;
    font-family: 'Roboto Condensed';
  }
  .dropdown-placeholder {
    color: #3e3e3e;
  }
`;

const Header = styled.div`
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`;

const CustomIcon = styled(Icon)`
  ${({ theme }) => theme.utils.spinAnimation};
  position: absolute;
  fill: #f9fafa;
`;
